import {
  mapThreadMessage,
  type ChatMessage,
  type ProactiveGreetingPage,
  type ThreadApiMessage,
  type WidgetSendHistoryItem,
  WIDGET_SEND_HISTORY_MAX,
} from '@teo/widget-shared';
import {
  getWidgetAuthHeaders,
  getWidgetSession,
  clearWidgetSession,
  updateWidgetSessionThreadId,
  type WidgetSession,
} from '../session/widget-session.js';
import { resolveWidgetMessageId } from './create-client-id.js';
import {
  createHttpUploadFile,
  createMockUploadFile,
  WidgetUploadError,
} from './upload-thread-file.js';

export type SendMessageRequest = {
  threadId: number | string | null;
  text: string;
  /** Passed to backend for optimistic reconciliation */
  clientId: string;
  kind?: OutgoingMessageKind;
  /** Greeting offer to import before this user message (`history` on send). */
  history?: WidgetSendHistoryItem[];
  /** Idempotent commit key from proactive greeting offer. */
  greetingId?: string;
  /** Page snapshot — first send on this pathname only. */
  page?: ProactiveGreetingPage;
  /** Stable client send id (`wid` + UUID without hyphens). */
  messageId?: string;
};

export type SendMessageResult =
  | { message: ThreadApiMessage }
  | { queued: true; text: string };

export type SendMessageFn = (
  request: SendMessageRequest,
) => Promise<SendMessageResult>;

/** Text message constants for `POST /api/widget/send`. */
export const WIDGET_TEXT_MESSAGE_TYPE = 1;
export const WIDGET_TEXT_PAYLOAD_TYPE = 1;

/** Image message constants for `POST /api/widget/send`. */
export const WIDGET_IMAGE_MESSAGE_TYPE = 2;
export const WIDGET_IMAGE_PAYLOAD_TYPE = 2;

export type OutgoingMessageKind = 'text' | 'image';

export type WidgetSendPayload = {
  message: string;
  message_payload_type: number;
  messageType: number;
  company_id: string;
  user_id: string;
  history?: WidgetSendHistoryItem[];
  greeting_id?: string;
  page?: ProactiveGreetingPage;
  /** Present on every outbound send: `wid` + UUID without hyphens. */
  message_id: string;
};

const WIDGET_SEND_PATH = '/api/widget/send';

export function buildWidgetSendUrl(apiBase: string): string {
  const base = apiBase.replace(/\/$/, '');
  return `${base}${WIDGET_SEND_PATH}`;
}

export type BuildWidgetSendPayloadOptions = {
  kind?: OutgoingMessageKind;
  history?: WidgetSendHistoryItem[];
  greetingId?: string;
  page?: ProactiveGreetingPage;
  messageId?: string;
};

export function buildWidgetSendPayload(
  text: string,
  session: WidgetSession,
  options?: BuildWidgetSendPayloadOptions,
): WidgetSendPayload {
  const kind = options?.kind ?? 'text';
  const isImage = kind === 'image';
  const payload: WidgetSendPayload = {
    message: text,
    message_payload_type: isImage
      ? WIDGET_IMAGE_PAYLOAD_TYPE
      : WIDGET_TEXT_PAYLOAD_TYPE,
    messageType: isImage ? WIDGET_IMAGE_MESSAGE_TYPE : WIDGET_TEXT_MESSAGE_TYPE,
    company_id: String(session.companyId),
    user_id: session.visitorId,
    message_id: resolveWidgetMessageId(options?.messageId),
  };

  if (options?.history && options.history.length > 0) {
    payload.history = options.history.slice(0, WIDGET_SEND_HISTORY_MAX);
  }

  const greetingId = options?.greetingId?.trim();
  if (greetingId) {
    payload.greeting_id = greetingId;
  }

  if (options?.page) {
    payload.page = options.page;
  }

  return payload;
}

export function mapQueuedSendToChatMessage(
  text: string,
  clientId: string,
  kind: OutgoingMessageKind = 'text',
): ChatMessage {
  const isImage = kind === 'image';
  return {
    id: clientId,
    clientId,
    role: 'user',
    text: isImage ? '' : text,
    createdAt: new Date().toISOString(),
    status: 'sent',
    contentType: isImage ? 'image' : 'text',
    mediaUrl: isImage ? text : undefined,
    attachment: isImage
      ? { kind: 'image', url: text }
      : undefined,
  };
}

export function mapSendResultToChatMessage(
  result: SendMessageResult,
  clientId: string,
  kind: OutgoingMessageKind = 'text',
): ChatMessage {
  if ('queued' in result) {
    return mapQueuedSendToChatMessage(result.text, clientId, kind);
  }

  const mapped = mapThreadMessage(result.message);
  if (!mapped) {
    throw new Error('[Teo Widget] Send response mapped to hidden message');
  }

  return {
    ...mapped,
    clientId,
    status: 'sent',
  };
}

function isThreadApiMessage(value: unknown): value is ThreadApiMessage {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    'message_role' in value &&
    typeof (value as ThreadApiMessage).message === 'string'
  );
}

function isQueuedAck(data: unknown): data is string {
  return typeof data === 'string';
}

function parseSendResponse(
  data: unknown,
  text: string,
): SendMessageResult {
  if (isQueuedAck(data)) {
    return { queued: true, text };
  }

  if (typeof data !== 'object' || data === null) {
    throw new Error('[Teo Widget] Invalid send response');
  }

  if ('message' in data && isThreadApiMessage(data.message)) {
    return { message: data.message };
  }

  if (isThreadApiMessage(data)) {
    return { message: data };
  }

  throw new Error('[Teo Widget] Invalid send response');
}

/**
 * POST /api/widget/send — message is queued on the backend (RabbitMQ).
 * Response is often `"Message added to Rabbit Queue"` rather than a thread message.
 */
export function createHttpSendMessage(apiBase: string): SendMessageFn {
  return async ({ text, clientId, kind = 'text', history, greetingId, page, messageId }) => {
    const session = getWidgetSession();
    if (!session) {
      throw new Error('[Teo Widget] Session required to send message');
    }

    const url = buildWidgetSendUrl(apiBase);
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...getWidgetAuthHeaders(),
      },
      body: JSON.stringify(
        buildWidgetSendPayload(text, session, {
          kind,
          history,
          greetingId,
          page,
          messageId,
        }),
      ),
    });

    if (!response.ok) {
      if (response.status === 401) {
        clearWidgetSession(session.slug);
      }
      throw new Error(`[Teo Widget] Send failed (${response.status})`);
    }

    const data: unknown = await response.json();
    const result = parseSendResponse(data, text);

    if ('message' in result) {
      const newThreadId = result.message.thread_id;
      if (newThreadId && !session.threadId) {
        updateWidgetSessionThreadId(newThreadId);
      }
    }

    return result;
  };
}

export type SendImageMessageRequest = {
  file: File;
  threadId: number | string | null;
  clientId: string;
  /** Called after file is stored on server, before `POST /api/widget/send`. */
  onUploadComplete?: (uploaded: {
    url: string;
    threadId: number | string;
  }) => void;
  page?: ProactiveGreetingPage;
  messageId?: string;
};

export type SendImageMessageFn = (
  request: SendImageMessageRequest,
) => Promise<SendMessageResult>;

/** Upload image to thread storage, then queue via `POST /api/widget/send`. */
export function createHttpSendImageMessage(
  apiBase: string,
  uploadFile = createHttpUploadFile(apiBase),
  sendMessage = createHttpSendMessage(apiBase),
): SendImageMessageFn {
  return async ({ file, threadId, clientId, onUploadComplete, page, messageId }) => {
    const session = getWidgetSession();
    const resolvedThreadId =
      (threadId != null && Number(threadId) > 0
        ? threadId
        : session?.threadId) ?? null;

    if (!resolvedThreadId) {
      throw new WidgetUploadError(
        'NO_THREAD',
        '[Teo Widget] thread_id is required before image upload',
      );
    }

    const uploaded = await uploadFile({
      file,
      threadId: resolvedThreadId,
    });

    onUploadComplete?.({
      url: uploaded.url,
      threadId: uploaded.threadId,
    });

    return sendMessage({
      threadId: uploaded.threadId,
      text: uploaded.url,
      clientId,
      kind: 'image',
      page,
      messageId,
    });
  };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Dev / Storybook transport with configurable latency and failure trigger. */
export function createMockSendMessage(options?: {
  latencyMs?: number;
  failText?: string;
}): SendMessageFn {
  const latencyMs = options?.latencyMs ?? 900;
  const failText = options?.failText ?? '__fail__';
  let seq = 0;

  return async ({ text, clientId, threadId, kind = 'text', history }) => {
    await delay(latencyMs);

    if (text.trim() === failText) {
      throw new Error('[Teo Widget] Mock send failed');
    }

    const session = getWidgetSession();
    let resolvedThreadId =
      threadId != null && Number(threadId) > 0
        ? Number(threadId)
        : session?.threadId;

    if (!resolvedThreadId || Number(resolvedThreadId) <= 0) {
      resolvedThreadId = 5753;
      updateWidgetSessionThreadId(resolvedThreadId);
    }

    seq += 1;
    const now = new Date().toISOString();
    const isImage = kind === 'image';

    return {
      message: {
        id: 900_000 + seq,
        thread_id: resolvedThreadId,
        lead_follow_up_template_id: null,
        message_role: 2,
        message: text,
        raw_message: {
          type: isImage ? 'image_url' : 'text',
          payload: text,
          datetime: now,
        },
        type: isImage ? 2 : 1,
        like: 0,
        tokens: 0,
        source_id: 300,
        created_at: now,
        updated_at: now,
      },
    };
  };
}

export function createMockSendImageMessage(options?: {
  latencyMs?: number;
  uploadLatencyMs?: number;
}): SendImageMessageFn {
  const upload = createMockUploadFile({
    latencyMs: options?.uploadLatencyMs ?? 600,
  });
  const send = createMockSendMessage({ latencyMs: options?.latencyMs ?? 500 });
  return createHttpSendImageMessage('', upload, send);
}
